import type { ReactNode } from "react";

/**
 * Marquee - reusable seamless scrolling strip (Wave 0 foundation).
 *
 * Renders `children` TWICE inside one `width:max-content` flex track. The
 * track translates 0 -> -50% (`marquee`) or -50% -> 0 (`marquee-rev`), so
 * the duplicated half scrolls in to replace the first half with no visible
 * seam. The outer wrapper carries an edge mask so items fade at both ends.
 *
 * The animated track carries `home-anim` so the homepage reduced-motion
 * exception keeps it moving (see theme.css). Used 4x: EMR strip (28s left),
 * payer row A (36s left), payer row B (32s right), investor strip (30s left,
 * inside a window as wide as its sentence).
 */
interface MarqueeProps {
  children: ReactNode;
  durationSeconds: number;
  direction?: "left" | "right";
  className?: string;
  "aria-label"?: string;
}

export function Marquee({
  children,
  durationSeconds,
  direction = "left",
  className,
  "aria-label": ariaLabel,
}: MarqueeProps) {
  const keyframe = direction === "right" ? "marquee-rev" : "marquee";
  return (
    <div
      className={className}
      aria-label={ariaLabel}
      style={{
        position: "relative",
        width: "100%",
        WebkitMaskImage:
          "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
        maskImage:
          "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
      }}
    >
      <div
        className="home-anim"
        style={{
          display: "flex",
          width: "max-content",
          animation: `${keyframe} ${durationSeconds}s linear infinite`,
        }}
      >
        {children}
        {children}
      </div>
    </div>
  );
}
